import { gql } from "@apollo/client";
import {
  Campaign,
  Course,
  FormItem,
  Section,
  Team,
  Template,
  Response,
  User,
  Results,
  MyResponse,
  GroupCategory,
  LoopResponse,
  AssignmentSubmission,
  Analysis,
  AggregateComments,
  OverallValues,
  MatchingConfig,
  MatchingRun,
  MatchingSurveyData,
  MatchingSuccess,
} from "./types";

export interface SectionsResult {
  sections: Section[];
}

export const item = `
  items(order: { order: ASC }) {
    id
    category
    itemType
    questionType
    label
    optional
    minVariance
    options {
      id
      label
      order
      value
    }
    order
    recipientType
    scale
    subjectType
    visibility
    pointsAllocationMode
  }
`;

export const GET_SECTIONS = gql`
  query GetSections($filters: SectionFilter) {
    sections(filters: $filters) {
      id
      name
      course {
        id
        name
      }
      userIds
    }
  }
`;

export interface GroupsResult {
  groups: Team[];
}

export const GET_GROUPS = gql`
  query GetGroups($filters: GroupFilter) {
    groups(filters: $filters, order: { name: ASC }) {
      id
      leaderId
      name
      userIds
      hasInstructor
    }
  }
`;

export const ADD_GROUP = gql`
  mutation AddGroup(
    $name: String!
    $categoryId: String
    $courseId: String
    $userIds: [String!]
  ) {
    addGroup(
      name: $name
      categoryId: $categoryId
      courseId: $courseId
      userIds: $userIds
    ) {
      ... on Group {
        id
        leaderId
        name
        userIds
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const UPDATE_GROUP = gql`
  mutation UpdateGroup($id: String!, $name: String, $userIds: [String!]) {
    updateGroup(id: $id, name: $name, userIds: $userIds) {
      ... on Group {
        id
        leaderId
        name
        userIds
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const SET_GROUP_LEADER = gql`
  mutation SetGroupLeader($id: String!, $leaderId: String) {
    setGroupLeader(id: $id, leaderId: $leaderId) {
      ... on Group {
        id
        leaderId
        name
        userIds
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const DELETE_GROUP = gql`
  mutation DeleteGroup($id: String!) {
    deleteGroup(id: $id)
  }
`;

export interface UsersResult {
  users: User[];
}

export const GET_USERS = gql`
  query GetUsers($filters: UserFilter) {
    users(filters: $filters) {
      id
      email
      headshot
      firstName
      lastName
      ssoId
      isLearner
      isInstructor
    }
  }
`;

export interface TeamsUsersResult {
  allUsers: User[];
  sectionUsers: User[];
}

export const GET_ALL_AND_SECTION_USERS = gql`
  query GetUsers($filters: UserFilter) {
    allUsers: users {
      id
      email
      headshot
      firstName
      lastName
      ssoId
      isLearner
      isInstructor
    }
    sectionUsers: users(filters: $filters) {
      id
      email
      headshot
      firstName
      lastName
      ssoId
      isLearner
      isInstructor
    }
  }
`;

export interface FormsResult {
  forms: Template[];
}

export const GET_FORMS = gql`
  query GetForms($filters: FormFilter) {
    forms(filters: $filters) {
      id
      active
      instructions
      modified
      name
      templateType
      ${item}
      institutionCreated
      isDirectShare
      isGlobal
      isShared
      isSnapshot
      isMine
      isUsedInDraftOrScheduled
      user {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

export interface FormResult {
  form: Template;
}

export const GET_FORM = gql`
  query GetForm($pk: ID!) {
    form(pk: $pk) {
      id
      active
      instructions
      modified
      name
      templateType
      ${item}
      institutionCreated
      isDirectShare
      isGlobal
      isShared
      isSnapshot
      isMine
      isUsedInDraftOrScheduled
      user {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

export interface AddFormResult {
  addForm: Template;
}

export const ADD_FORM = gql`
  mutation AddForm($name: String!, $templateType: String) {
    addForm(name: $name, templateType: $templateType) {
      ... on Form {
        id
        active
        instructions
        modified
        name
        templateType
        ${item}
        institutionCreated
        isDirectShare
        isGlobal
        isShared
        isSnapshot
        isMine
        isUsedInDraftOrScheduled
        user {
          id
          email
          firstName
          lastName
        }
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface CloneFormResult {
  cloneForm: Template;
}

export const CLONE_FORM = gql`
  mutation CloneForm($id: String!) {
    cloneForm(id: $id) {
      ... on Form {
        id
        active
        instructions
        modified
        name
        ${item}
        institutionCreated
        isDirectShare
        isGlobal
        isShared
        isSnapshot
        isMine
        isUsedInDraftOrScheduled
        user {
          id
          email
          firstName
          lastName
        }
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const DELETE_FORM = gql`
  mutation DeleteForm($id: String!) {
    deleteForm(id: $id)
  }
`;

export interface ShareFormResult {
  shareForm: Template;
}

export const SHARE_FORM = gql`
  mutation ShareForm($id: String!, $share: Boolean!) {
    shareForm(id: $id, share: $share) {
      ... on Form {
        id
        active
        instructions
        modified
        name
        ${item}
        institutionCreated
        isDirectShare
        isGlobal
        isShared
        isSnapshot
        isMine
        isUsedInDraftOrScheduled
        user {
          id
          email
          firstName
          lastName
        }
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface UpdateFormResult {
  updateForm: Template;
}

export const UPDATE_FORM = gql`
  mutation UpdateForm(
    $id: String!
    $instructions: String
    $name: String
    $items: [FormItemInput!]
  ) {
    updateForm(
      id: $id
      instructions: $instructions
      name: $name
      items: $items
    ) {
      ... on Form {
        id
        active
        instructions
        modified
        name
        ${item}
        institutionCreated
        isDirectShare
        isGlobal
        isShared
        isSnapshot
        isMine
        isUsedInDraftOrScheduled
        user {
          id
          email
          firstName
          lastName
        }
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface FormItemsResult {
  formItems: FormItem[];
}

export const GET_FORMITEMS = gql`
  query GetFormItems($filters: FormItemFilter) {
    formItems(filters: $filters) {
      id
      category
      itemType
      label
      optional
      minVariance
      options {
        id
        label
        order
        value
      }
      order
      recipientType
      questionType
      scale
      subjectType
      visibility
    }
  }
`;

export interface CampaignsResult {
  campaigns: Campaign[];
}

export const GET_CAMPAIGNS = gql`
  query GetCampaigns($filters: CampaignFilter) {
    campaigns(filters: $filters) {
      id
      activity {
        id
        created
        label
      }
      assignmentEnabled
      assignmentGradingMode
      assignmentPoints
      assignmentRatingProportion
      campaignType
      course {
        id
        name
      }
      dueDate
      formId
      form {
        id
        name
      }
      groupcategory {
        id
        name
      }
      groups(order: { name: ASC }) {
        id
        leaderId
        name
        userIds
        isHidden
      }
      loopInstructor
      loopStudent
      name
      released
      sectionId
      state
      submittedResponses
      totalResponses
    }
  }
`;

export const ADD_CAMPAIGN = gql`
  mutation AddCampaign($name: String!, $formId: String, $sectionId: String, $dueDate: String, $campaignType: String) {
    addCampaign(name: $name, formId: $formId, sectionId: $sectionId, dueDate: $dueDate, campaignType: $campaignType) {
      ... on Campaign {
        id
        name
        state
        campaignType
        assignmentEnabled
        assignmentGradingMode
        assignmentPoints
        assignmentRatingProportion
        autoCloseDate
        autoReleaseDate
        autoSendDate
        course {
          id
          name
        }
        dueDate
        formId
        form {
          id
          active
          instructions
          modified
          name
          ${item}
          institutionCreated
          isDirectShare
          isGlobal
          isShared
          isSnapshot
          isMine
          isUsedInDraftOrScheduled
        }
        groupcategory {
          id
          name
        }
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        name
        released
        sectionId
        activity {
          id
          label
          created
        }
        submittedResponses
        totalResponses
        gradesSyncedToLms
        loopInstructor
        loopStudent
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const UPDATE_CAMPAIGN = gql`
  mutation UpdateCampaign(
    $id: String!
    $name: String
    $formId: String
    $sectionId: String
    $state: String
    $dueDate: DateTime
    $autoCloseDate: DateTime
    $autoReleaseDate: DateTime
    $autoSendDate: DateTime
    $released: DateTime
    $activityLabel: String
    $assignmentEnabled: Boolean
    $assignmentGradingMode: String
    $assignmentPoints: String
    $assignmentRatingProportion: Int
    $loopInstructor: Boolean
    $loopStudent: Boolean
    $campaignType: String
    $assignmentDescription: String
    $assignmentSendDate: DateTime
    $assignmentDueDate: DateTime
    $assignmentCloseDate: DateTime
    $submissionType: String
  ) {
    updateCampaign(
      id: $id
      name: $name
      formId: $formId
      sectionId: $sectionId
      state: $state
      dueDate: $dueDate
      autoCloseDate: $autoCloseDate
      autoReleaseDate: $autoReleaseDate
      autoSendDate: $autoSendDate
      released: $released
      activityLabel: $activityLabel
      assignmentEnabled: $assignmentEnabled
      assignmentGradingMode: $assignmentGradingMode
      assignmentPoints: $assignmentPoints
      assignmentRatingProportion: $assignmentRatingProportion
      loopInstructor: $loopInstructor
      loopStudent: $loopStudent
      campaignType: $campaignType
      assignmentDescription: $assignmentDescription
      assignmentSendDate: $assignmentSendDate
      assignmentDueDate: $assignmentDueDate
      assignmentCloseDate: $assignmentCloseDate
      submissionType: $submissionType
    ) {
      ... on Campaign {
        id
        activity {
          id
          created
          label
        }
        assignment {
          id
          description
          pdfAttachment
          pdfAttachmentUrl
          submissionType
          assignmentSendDate
          assignmentDueDate
          assignmentCloseDate
          submissions {
            id
            assignmentId
            authorId
            groupId
            status
            submissionText
            submissionFileUrl
            submittedDate
            grade
            instructorFeedback
            tokenizedLink
          }
        }
        assignmentEnabled
        assignmentGradingMode
        assignmentPoints
        assignmentRatingProportion
        autoCloseDate
        autoReleaseDate
        autoSendDate
        campaignType
        dueDate
        formId
        form {
          id
          active
          instructions
          modified
          name
          ${item}
          institutionCreated
          isDirectShare
          isGlobal
          isShared
          isSnapshot
          isMine
          isUsedInDraftOrScheduled
        }
        groupcategory {
          id
          name
        }
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        loopInstructor
        loopStudent
        name
        released
        sectionId
        state
        submittedResponses
        totalResponses
        gradesSyncedToLms
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const UPDATE_ASSIGNMENT_POINTS = gql`
  mutation UpdateAssignmentPoints($id: String!, $assignmentPoints: String!) {
    updateAssignmentPoints(id: $id, assignmentPoints: $assignmentPoints) {
      ... on Campaign {
        id
        activity {
          id
          created
          label
        }
        assignmentEnabled
        assignmentGradingMode
        assignmentPoints
        assignmentRatingProportion
        dueDate
        formId
        form {
          id
          active
          instructions
          modified
          name
          ${item}
          institutionCreated
          isDirectShare
          isGlobal
          isShared
          isSnapshot
          isMine
          isUsedInDraftOrScheduled
        }
        groupcategory {
          id
          name
        }
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        name
        released
        sectionId
        state
        submittedResponses
        totalResponses
        gradesSyncedToLms
        loopStudent
        loopInstructor
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const SEND_CAMPAIGN_REMINDERS = gql`
  mutation SendCampaignReminders($id: String!) {
    sendCampaignReminders(id: $id)
  }
`;

export const DELETE_CAMPAIGN = gql`
  mutation DeleteCampaign($id: String!) {
    deleteCampaign(id: $id)
  }
`;

export interface CampaignResult {
  campaign: Campaign;
}

export const GET_CAMPAIGN = gql`
  query GetCampaign($pk: ID!) {
    campaign(pk: $pk) {
      id
      activity {
        id
        created
        label
      }
      assignment {
        id
        description
        pdfAttachment
        pdfAttachmentUrl
        submissionType
        assignmentSendDate
        assignmentDueDate
        assignmentCloseDate
        submissions {
          id
          assignmentId
          authorId
          groupId
          status
          submissionText
          submissionFileUrl
          submittedDate
          grade
          instructorFeedback
          tokenizedLink
        }
      }
      assignmentEnabled
      assignmentGradingMode
      assignmentPoints
      assignmentRatingProportion
      autoCloseDate
      autoReleaseDate
      autoSendDate
      campaignType
      course {
        id
        name
        lmsType
        oauthUrl
        gradePushCapable
        gradePushReady
        fullSyncEnabled
        fullSyncReady
      }
      dueDate
      formId
      form {
        id
        active
        instructions
        modified
        name
        ${item}
        institutionCreated
        isDirectShare
        isGlobal
        isShared
        isSnapshot
        isMine
        isUsedInDraftOrScheduled
      }
      groupcategory {
        id
        name
      }
      groups(order: { name: ASC }) {
        id
        leaderId
        name
        userIds
        isHidden
        hasInstructor
      }
      name
      released
      sectionId
      state
      submittedResponses
      totalResponses
      gradesSyncedToLms
      sendBlockReason
      loopInstructor
      loopStudent
      loopFeedbackSummary {
        subjectId
        hasStudentFeedback
        hasInstructorFeedback
      }
    }
  }
`;

export interface ResponsesResult {
  responses: Response[];
}

export const GET_RESPONSES = gql`
  query GetResponses($filters: ResponseFilter) {
    responses(filters: $filters) {
      id
      authorId
      campaignId
      groupId
      isTeamLead
      submitted
      submittedDate
      hasAnswers
      hasReceivedFeedback
      responseSubjects {
        id
        answers {
          id
          itemId
          modified
          text
          value
        }
        subjectType
        subjectUserId
        isTeamLead
        subjectUserIsInstructor
        subjectUserIsLearner
        completed
      }
      tokenizedLink
    }
  }
`;

export const GET_RESPONSES_SUMMARY = gql`
  query GetResponsesSummary($filters: ResponseFilter) {
    responses(filters: $filters) {
      id
      authorId
      campaignId
      groupId
      isTeamLead
      submitted
      submittedDate
      tokenizedLink
      campaignDueDate
      hasAnswers
      hasReceivedFeedback
    }
  }
`;

export interface MyResponsesResult {
  responses: MyResponse[];
}

export const GET_MY_RESPONSES = gql`
  query GetMyResponses($filters: ResponseFilter) {
    responses(filters: $filters) {
      id
      authorId
      campaignId
      campaignName
      campaignDueDate
      campaignState
      campaignReleased
      campaignResultsLink
      campaignLoopStudent
      hasLoopResponse
      groupId
      isTeamLead
      submitted
      tokenizedLink
    }
  }
`;

export interface ResponseResult {
  response: Response;
}

export const GET_RESPONSE = gql`
  query GetResponse($pk: ID!) {
    response(pk: $pk) {
      id
      authorId
      campaignId
      campaignName
      campaignState
      campaignType
      groupId
      isTeamLead
      form {
        id
        active
        instructions
        modified
        name
        ${item}
        userId
      }
      assignment {
        id
        description
        pdfAttachment
        pdfAttachmentUrl
        submissionType
        assignmentSendDate
        assignmentDueDate
        assignmentCloseDate
        submissions {
          id
          assignmentId
          authorId
          groupId
          status
          submissionText
          submissionFileUrl
          submittedDate
          grade
          instructorFeedback
          tokenizedLink
        }
      }
      submitted
      submittedDate
      hasAnswers
      hasReceivedFeedback
      responseSubjects {
        id
        answers {
          id
          itemId
          modified
          text
          value
        }
        subjectType
        subjectUserId
        isTeamLead
        subjectUserIsInstructor
        subjectUserIsLearner
        completed
      }
      tokenizedLink
    }
  }
`;

export const UPDATE_RESPONSE = gql`
  mutation UpdateResponse(
    $id: String!
    $submitted: Boolean
    $submittedDate: DateTime
    $responseSubjects: [ResponseSubjectInput!]
  ) {
    updateResponse(
      id: $id
      submitted: $submitted
      submittedDate: $submittedDate
      responseSubjects: $responseSubjects
    ) {
      ... on Response {
        id
        authorId
        campaignId
        groupId
        isTeamLead
        submitted
        submittedDate
        responseSubjects {
          id
          answers {
            id
            itemId
            modified
            text
            value
          }
          subjectType
          subjectUserId
          isTeamLead
          completed
        }
        tokenizedLink
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface ResultsResult {
  results: Results;
}

export const GET_RESULTS = gql`
  query Results($id: String!, $userId: String, $authorType: String) {
    results(id: $id, userId: $userId, authorType: $authorType) {
      id
      analyses {
        question {
          id
          category
          itemType
          questionType
          label
        }
        self
        others
        gap
        user {
          id
          email
          firstName
          lastName
        }
        label
        id
        outlier
      }
      aggregates {
        comments {
          id
          comment
          respondent {
            id
            email
            firstName
            lastName
          }
          recipient {
            id
            email
            firstName
            lastName
          }
        }
        question {
          id
          category
          itemType
          questionType
          label
          subjectType
        }
        id
      }
      overall {
        self
        given
        received
      }
      campaign {
        dueDate
        formId
        groupcategory {
          id
          name
        }
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        id
        name
        released
        sectionId
        state
        gradesSyncedToLms
        loopStudent
        loopInstructor
        course {
          id
          hideOverallAverages
        }
      }
      form {
        id
        active
        instructions
        modified
        name
        ${item}
        userId
      }
      user {
        id
        email
        headshot
        firstName
        lastName
        ssoId
      }
      hasMixedAuthors
    }
  }
`;

export interface InstructorResultsResult {
  instructorResults: {
    id: string;
    analyses: Analysis[];
    aggregates: AggregateComments[];
    pointRows: {
      id: string;
      question: FormItem;
      value: number;
      user: User;
      sender: User;
    }[];
    overall: OverallValues;
    campaign: Campaign;
    form: Template;
    studentAverages: Analysis[];
    teamAverages: Analysis[];
    availableTeams: Team[];
    availableUsers: User[];
    availableCampaigns: { id: string; name: string }[];
    hasMixedAuthors: boolean;
  };
}

export const GET_INSTRUCTOR_RESULTS = gql`
  query InstructorResults($id: String!, $subjectIds: [String!], $teamIds: [String!], $responseSubjectType: String, $authorType: String) {
    instructorResults(id: $id, subjectIds: $subjectIds, teamIds: $teamIds, responseSubjectType: $responseSubjectType, authorType: $authorType) {
      id
      analyses {
        question { id category itemType questionType label }
        self
        others
        gap
        given
        user { id email firstName lastName }
        label
        id
        outlier
      }
      aggregates {
        comments {
          id
          comment
          respondent { id email firstName lastName }
          recipient { id email firstName lastName }
        }
        question { id category itemType questionType label subjectType }
        id
      }
      pointRows {
        id
        question { id category itemType questionType label }
        value
        user { id email firstName lastName }
        sender { id email firstName lastName }
        label
      }
      overall { self given received }
      campaign {
        dueDate
        formId
        groupcategory { id name }
        groups(order: { name: ASC }) { id leaderId name userIds hasInstructor }
        id
        name
        released
        sectionId
        state
        gradesSyncedToLms
        loopStudent
        loopInstructor
      }
      form {
        id
        active
        instructions
        modified
        name
        ${item}
        userId
      }
      availableTeams { id name leaderId userIds }
      availableUsers { id email firstName lastName ssoId isInstructor }
      availableCampaigns { id name }
      studentAverages {
        id
        label
        self
        others
        gap
        given
        user { id email firstName lastName }
        question { id category itemType questionType label }
        outlier
      }
      teamAverages {
        id
        label
        self
        others
        gap
        given
        user { id email firstName lastName }
        question { id category itemType questionType label }
        outlier
      }
      hasMixedAuthors
    }
  }
`;

export interface CourseResult {
  course: Course;
}

export const GET_COURSE = gql`
  query GetCourse($pk: String) {
    course(pk: $pk) {
      id
      name
      fullSyncCapable
      fullSyncReady
      fullSyncEnabled
      lmsType
      oauthUrl
      includeInstructorsInTeams
      hideOverallAverages
    }
  }
`;

export const OBTAIN_LMS_TOKEN = gql`
  mutation ObtainLmsToken($state: String!, $code: String!) {
    obtainLmsToken(state: $state, code: $code)
  }
`;

export const CHECK_BLUEPRINT_IMPORT = gql`
  mutation CheckBlueprintImport {
    checkBlueprintImport
  }
`;

export const SET_COURSE_MODE = gql`
  mutation SetCourseMode($pk: String, $fullSync: Boolean!) {
    setCourseMode(pk: $pk, fullSync: $fullSync) {
      ... on Course {
        id
        name
        fullSyncCapable
        fullSyncReady
        fullSyncEnabled
        lmsType
        oauthUrl
        includeInstructorsInTeams
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const SET_INCLUDE_INSTRUCTORS_IN_TEAMS = gql`
  mutation SetIncludeInstructorsInTeams($pk: String, $includeInstructors: Boolean!) {
    setIncludeInstructorsInTeams(pk: $pk, includeInstructors: $includeInstructors) {
      ... on Course {
        id
        name
        includeInstructorsInTeams
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const SET_HIDE_OVERALL_AVERAGES = gql`
  mutation SetHideOverallAverages($pk: String, $hide: Boolean!) {
    setHideOverallAverages(pk: $pk, hide: $hide) {
      ... on Course {
        id
        hideOverallAverages
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const SYNC_COURSE = gql`
  mutation SyncCourse($pk: String) {
    syncCourse(pk: $pk) {
      ... on Course {
        id
        name
        fullSyncCapable
        fullSyncReady
        fullSyncEnabled
        lmsType
        oauthUrl
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const SYNC_CAMPAIGN_TEAMS = gql`
  mutation SyncCampaignTeams($id: String!, $reimport: Boolean) {
    syncCampaignTeams(id: $id, reimport: $reimport) {
      ... on Campaign {
        id
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        groupcategory {
          id
          name
        }
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const GET_ROLE = gql`
  query GetRole {
    myRole
    isInstanceAdmin
    hasCourseContext
  }
`;

export interface RoleResult {
  myRole: string;
  isInstanceAdmin: boolean;
  hasCourseContext: boolean;
}

export const EXCLUDE_GROUP = gql`
  mutation ExcludeGroup($campaignId: String!, $groupId: String!) {
    excludeGroup(campaignId: $campaignId, groupId: $groupId)
  }
`;

export interface GroupCategoriesResult {
  groupCategories: GroupCategory[];
}

export const GET_GROUP_CATEGORIES = gql`
  query GetGroupCategories($filters: GroupCategoryFilter) {
    groupCategories(filters: $filters) {
      id
      name
      course {
        id
        name
      }
    }
  }
`;

export const SET_CAMPAIGN_GROUPCATEGORY = gql`
  mutation SetCampaignGroupcategory($id: String!, $groupcategoryId: String) {
    setCampaignGroupcategory(id: $id, groupcategoryId: $groupcategoryId) {
      ... on Campaign {
        id
        activity {
          id
          created
          label
        }
        assignmentEnabled
        assignmentGradingMode
        assignmentPoints
        dueDate
        formId
        form {
          id
          active
          instructions
          modified
          name
          ${item}
          institutionCreated
          isDirectShare
          isGlobal
          isShared
          isSnapshot
          isMine
          isUsedInDraftOrScheduled
        }
        groupcategory {
          id
          name
        }
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        name
        released
        sectionId
        state
        submittedResponses
        totalResponses
        gradesSyncedToLms
        loopStudent
        loopInstructor
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const CLAIM_LICENSE = gql`
  mutation ClaimLicense($tokenString: String!) {
    claimLicense(tokenString: $tokenString) {
      ... on LicenseToken {
        id
      }
    }
  }
`;

export const GET_LICENSE = gql`
  query MyLicense {
    myLicense
    paymentMode
  }
`;

export interface LicenseResult {
  myLicense: string;
  paymentMode: string | null;
}

export const PUSH_CAMPAIGN_GRADES = gql`
  mutation PushCampaignGrades($id: String!) {
    pushCampaignGrades(id: $id)
  }
`;

export const GET_COURSE_GRADE_PUSH_STATE = gql`
  query GetCourseGradePushState {
    course {
      id
      lmsType
      oauthUrl
      gradePushCapable
      gradePushReady
    }
  }
`;

export const UPDATE_ASSIGNMENT = gql`
  mutation UpdateAssignment(
    $assignmentId: String!
    $description: String
    $submissionType: String
    $assignmentSendDate: DateTime
    $assignmentDueDate: DateTime
    $assignmentCloseDate: DateTime
  ) {
    updateAssignment(
      assignmentId: $assignmentId
      description: $description
      submissionType: $submissionType
      assignmentSendDate: $assignmentSendDate
      assignmentDueDate: $assignmentDueDate
      assignmentCloseDate: $assignmentCloseDate
    ) {
      id
      description
      pdfAttachment
      pdfAttachmentUrl
      submissionType
      assignmentSendDate
      assignmentDueDate
      assignmentCloseDate
    }
  }
`;

export const UPDATE_CAMPAIGN_ASSIGNMENT = gql`
  mutation UpdateCampaignAssignment(
    $id: String!
    $assignmentEnabled: Boolean
    $assignmentGradingMode: String
    $assignmentPoints: String
    $assignmentRatingProportion: Int
  ) {
    updateCampaignAssignment(
      id: $id
      assignmentEnabled: $assignmentEnabled
      assignmentGradingMode: $assignmentGradingMode
      assignmentPoints: $assignmentPoints
      assignmentRatingProportion: $assignmentRatingProportion
    ) {
      ... on Campaign {
        id
        activity {
          id
          created
          label
        }
        assignmentEnabled
        assignmentGradingMode
        assignmentPoints
        assignmentRatingProportion
        dueDate
        formId
        form {
          id
          active
          instructions
          modified
          name
          ${item}
          institutionCreated
          isDirectShare
          isGlobal
          isShared
          isSnapshot
          isMine
          isUsedInDraftOrScheduled
        }
        groupcategory {
          id
          name
        }
        groups(order: { name: ASC }) {
          id
          leaderId
          name
          userIds
          hasInstructor
        }
        name
        released
        sectionId
        state
        submittedResponses
        totalResponses
        gradesSyncedToLms
        loopStudent
        loopInstructor
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface UserCoursesResult {
  userCourses: Course[];
}

export const GET_USER_COURSES = gql`
  query GetUserCourses {
    # This is a placeholder that will be implemented later
    # when full site navigation support is added
    userCourses {
      id
      name
    }
  }
`;

export interface SiteDashboardDataResult {
  siteDashboardData: Campaign[];
}

export const GET_SITE_DASHBOARD_DATA = gql`
  query GetSiteDashboardData {
    # This is a placeholder that will be implemented later
    # when full site navigation support is added
    siteDashboardData {
      id
      name
      state
    }
  }
`;

export interface SiteMetricsResult {
  siteMetrics: {
    uniqueCourses: number;
    totalSurveys: number;
    uniqueRecipients: number;
    uniqueInstructors: number;
  };
}

export const GET_SITE_METRICS = gql`
  query GetSiteMetrics(
    $startDate: DateTime
    $endDate: DateTime
    $coursePrefix: String
  ) {
    siteMetrics(
      startDate: $startDate
      endDate: $endDate
      coursePrefix: $coursePrefix
    ) {
      uniqueCourses
      totalSurveys
      uniqueRecipients
      uniqueInstructors
    }
  }
`;

export interface CourseMetrics {
  id: string;
  friendlyId: string;
  name: string;
  created: string;
  studentCount: number;
  instructorCount: number;
}

export interface SiteCourseMetricsResult {
  siteCourseMetrics: CourseMetrics[];
}

export const GET_SITE_COURSE_METRICS = gql`
  query GetSiteCourseMetrics(
    $startDate: DateTime
    $endDate: DateTime
    $coursePrefix: String
  ) {
    siteCourseMetrics(
      startDate: $startDate
      endDate: $endDate
      coursePrefix: $coursePrefix
    ) {
      id
      friendlyId
      name
      created
      studentCount
      instructorCount
    }
  }
`;

export const GET_SITE_INSTRUCTOR_METRICS = gql`
  query GetSiteInstructorMetrics(
    $startDate: DateTime!
    $endDate: DateTime!
    $coursePrefix: String
  ) {
    siteInstructorMetrics(
      startDate: $startDate
      endDate: $endDate
      coursePrefix: $coursePrefix
    ) {
      id
      name
      email
    }
  }
`;

export interface SiteInstructorMetricsResult {
  siteInstructorMetrics: {
    id: string;
    name: string;
    email: string;
  }[];
}

export interface SiteStudentMetricsResult {
  siteStudentMetrics: {
    id: string;
    name: string;
    email: string;
  }[];
}

export const GET_SITE_STUDENT_METRICS = gql`
  query GetSiteStudentMetrics(
    $startDate: DateTime!
    $endDate: DateTime!
    $coursePrefix: String
  ) {
    siteStudentMetrics(
      startDate: $startDate
      endDate: $endDate
      coursePrefix: $coursePrefix
    ) {
      id
      name
      email
    }
  }
`;

export const GET_LOOP_RESPONSES = gql`
  query GetLoopResponses($campaignId: String!, $subjectId: String!) {
    loopResponses(campaignId: $campaignId, subjectId: $subjectId) {
      id
      message
      sender {
        id
        firstName
        lastName
      }
      subject {
        id
        firstName
        lastName
      }
      loopType
    }
  }
`;

export const ADD_LOOP_RESPONSE = gql`
  mutation AddLoopResponse(
    $campaignId: String!
    $subjectId: String!
    $message: String!
  ) {
    addLoopResponse(
      campaignId: $campaignId
      subjectId: $subjectId
      message: $message
    ) {
      ... on LoopResponse {
        id
        message
        sender {
          id
          firstName
          lastName
        }
        subject {
          id
          firstName
          lastName
        }
        loopType
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface LoopResponsesResult {
  loopResponses: LoopResponse[];
}

export interface AssignmentSubmissionResult {
  assignmentSubmission: AssignmentSubmission;
}

export const GET_ASSIGNMENT_SUBMISSION = gql`
  query GetAssignmentSubmission($pk: ID!) {
    assignmentSubmission(pk: $pk) {
      id
      assignment {
        id
        description
        pdfAttachment
        pdfAttachmentUrl
        submissionType
        assignmentSendDate
        assignmentDueDate
        assignmentCloseDate
      }
      assignmentId
      authorId
      groupId
      status
      submissionText
      submissionFileUrl
      submittedDate
      grade
      instructorFeedback
      tokenizedLink
    }
  }
`;

export const UPDATE_ASSIGNMENT_SUBMISSION = gql`
  mutation UpdateAssignmentSubmission(
    $id: String!
    $status: String
    $submissionText: String
    $submittedDate: DateTime
  ) {
    updateAssignmentSubmission(
      id: $id
      status: $status
      submissionText: $submissionText
      submittedDate: $submittedDate
    ) {
      ... on AssignmentSubmission {
        id
        assignment {
          id
          description
          pdfAttachment
          pdfAttachmentUrl
          submissionType
          assignmentSendDate
          assignmentDueDate
          assignmentCloseDate
        }
        assignmentId
        authorId
        groupId
        status
        submissionText
        submissionFileUrl
        submittedDate
        grade
        instructorFeedback
        tokenizedLink
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const GET_ASSIGNMENT_SUBMISSION_UPLOAD_URL = gql`
  mutation GetAssignmentSubmissionUploadUrl(
    $submissionId: String!
    $filename: String!
    $fileSize: Int!
  ) {
    getAssignmentSubmissionUploadUrl(
      submissionId: $submissionId
      filename: $filename
      fileSize: $fileSize
    ) {
      ... on PresignedUploadUrl {
        uploadUrl
        fields
        fileUrl
        filename
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const CONFIRM_ASSIGNMENT_SUBMISSION_UPLOAD = gql`
  mutation ConfirmAssignmentSubmissionUpload(
    $submissionId: String!
    $fileUrl: String!
    $originalFilename: String!
  ) {
    confirmAssignmentSubmissionUpload(
      submissionId: $submissionId
      fileUrl: $fileUrl
      originalFilename: $originalFilename
    ) {
      ... on AssignmentSubmission {
        id
        assignment {
          id
          description
          pdfAttachment
          pdfAttachmentUrl
          submissionType
          assignmentSendDate
          assignmentDueDate
          assignmentCloseDate
        }
        assignmentId
        authorId
        groupId
        status
        submissionText
        submissionFileUrl
        submittedDate
        grade
        instructorFeedback
        tokenizedLink
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const GET_ASSIGNMENT_PDF_UPLOAD_URL = gql`
  mutation GetAssignmentPdfUploadUrl(
    $assignmentId: String!
    $filename: String!
    $fileSize: Int!
  ) {
    getAssignmentPdfUploadUrl(
      assignmentId: $assignmentId
      filename: $filename
      fileSize: $fileSize
    ) {
      ... on PresignedUploadUrl {
        uploadUrl
        fields
        fileUrl
        filename
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const CONFIRM_ASSIGNMENT_PDF_UPLOAD = gql`
  mutation ConfirmAssignmentPdfUpload(
    $assignmentId: String!
    $fileUrl: String!
    $originalFilename: String!
  ) {
    confirmAssignmentPdfUpload(
      assignmentId: $assignmentId
      fileUrl: $fileUrl
      originalFilename: $originalFilename
    ) {
      ... on Assignment {
        id
        description
        pdfAttachment
        pdfAttachmentUrl
        submissionType
        assignmentSendDate
        assignmentDueDate
        assignmentCloseDate
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export const DELETE_ASSIGNMENT_PDF_ATTACHMENT = gql`
  mutation DeleteAssignmentPdfAttachment($assignmentId: String!) {
    deleteAssignmentPdfAttachment(assignmentId: $assignmentId) {
      ... on Assignment {
        id
        description
        pdfAttachment
        pdfAttachmentUrl
        submissionType
        assignmentSendDate
        assignmentDueDate
        assignmentCloseDate
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

// ── Matching ──────────────────────────────────────────────────────────────────

const matchingQuestionFragment = `
  id
  questionType
  label
  description
  weight
  order
  options
  required
  similarityPreference
`;

const matchingConfigFragment = `
  id
  name
  description
  timePreferenceWeight
  rolePreferenceWeight
  personalityWeight
  preferredTeammatesWeight
  similarityPreference
  isTemplate
  created
  modified
  questions {
    ${matchingQuestionFragment}
  }
`;

const matchingOutputMemberFragment = `
  id
  userId
  name
  email
  order
`;

const matchingOutputTeamFragment = `
  id
  order
  name
  score
  rationale
  appliedGroupId
  members {
    ${matchingOutputMemberFragment}
  }
`;

const matchingRunFragment = `
  id
  configId
  courseId
  status
  teamSize
  numTeams
  surveyDueDate
  responseThreshold
  reminderEnabled
  reminderFrequency
  notes
  launchedAt
  processedAt
  appliedAt
  lmsSyncedAt
  score
  analysis
  errorMessage
  created
  modified
  responseCount
  tokenCount
  responseRate
  outputTeams {
    ${matchingOutputTeamFragment}
  }
`;

export interface MatchingConfigsResult {
  matchingConfigs: MatchingConfig[];
}

export const GET_MATCHING_CONFIGS = gql`
  query GetMatchingConfigs {
    matchingConfigs {
      ${matchingConfigFragment}
    }
  }
`;

export interface MatchingConfigResult {
  matchingConfig: MatchingConfig;
}

export const GET_MATCHING_CONFIG = gql`
  query GetMatchingConfig($id: ID!) {
    matchingConfig(id: $id) {
      ${matchingConfigFragment}
    }
  }
`;

export interface MatchingRunsResult {
  matchingRuns: MatchingRun[];
}

export const GET_MATCHING_RUNS = gql`
  query GetMatchingRuns($courseId: String) {
    matchingRuns(courseId: $courseId) {
      ${matchingRunFragment}
    }
  }
`;

export interface MatchingRunResult {
  matchingRun: MatchingRun;
}

export const GET_MATCHING_RUN = gql`
  query GetMatchingRun($id: ID!) {
    matchingRun(id: $id) {
      ${matchingRunFragment}
    }
  }
`;

export interface MatchingSurveyResult {
  matchingSurvey: MatchingSurveyData;
}

export const GET_MATCHING_SURVEY = gql`
  query GetMatchingSurvey($key: String!) {
    matchingSurvey(key: $key) {
      runId
      courseName
      dueDate
      notes
      alreadySubmitted
      questions {
        ${matchingQuestionFragment}
      }
      existingAnswers {
        questionId
        valueJson
      }
      roster {
        id
        name
        email
      }
    }
  }
`;

export interface CreateMatchingConfigResult {
  createMatchingConfig: MatchingConfig;
}

export const CREATE_MATCHING_CONFIG = gql`
  mutation CreateMatchingConfig($input: MatchingConfigInput!) {
    createMatchingConfig(input: $input) {
      ... on MatchingConfigType {
        ${matchingConfigFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface UpdateMatchingConfigResult {
  updateMatchingConfig: MatchingConfig;
}

export const UPDATE_MATCHING_CONFIG = gql`
  mutation UpdateMatchingConfig($id: String!, $input: MatchingConfigInput!) {
    updateMatchingConfig(id: $id, input: $input) {
      ... on MatchingConfigType {
        ${matchingConfigFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface DeleteMatchingConfigResult {
  deleteMatchingConfig: MatchingSuccess;
}

export const DELETE_MATCHING_CONFIG = gql`
  mutation DeleteMatchingConfig($id: String!) {
    deleteMatchingConfig(id: $id) {
      ok
      message
    }
  }
`;

export interface CreateMatchingRunResult {
  createMatchingRun: MatchingRun;
}

export const CREATE_MATCHING_RUN = gql`
  mutation CreateMatchingRun($input: MatchingRunInput!) {
    createMatchingRun(input: $input) {
      ... on MatchingRunType {
        ${matchingRunFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface LaunchMatchingSurveyResult {
  launchMatchingSurvey: MatchingRun;
}

export const LAUNCH_MATCHING_SURVEY = gql`
  mutation LaunchMatchingSurvey($runId: String!) {
    launchMatchingSurvey(runId: $runId) {
      ... on MatchingRunType {
        ${matchingRunFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface TriggerMatchingResult {
  triggerMatching: MatchingRun;
}

export const TRIGGER_MATCHING = gql`
  mutation TriggerMatching($runId: String!) {
    triggerMatching(runId: $runId) {
      ... on MatchingRunType {
        ${matchingRunFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface SaveMatchingOutputResult {
  saveMatchingOutput: MatchingRun;
}

export const SAVE_MATCHING_OUTPUT = gql`
  mutation SaveMatchingOutput($runId: String!, $teams: [MatchingOutputTeamInput!]!) {
    saveMatchingOutput(runId: $runId, teams: $teams) {
      ... on MatchingRunType {
        ${matchingRunFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface ApplyMatchingRunResult {
  applyMatchingRun: MatchingRun;
}

export const APPLY_MATCHING_RUN = gql`
  mutation ApplyMatchingRun(
    $runId: String!
    $groupCategoryId: String
    $newGroupSetName: String
  ) {
    applyMatchingRun(
      runId: $runId
      groupCategoryId: $groupCategoryId
      newGroupSetName: $newGroupSetName
    ) {
      ... on MatchingRunType {
        ${matchingRunFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface SyncMatchingTeamsToLmsResult {
  syncMatchingTeamsToLms: MatchingRun;
}

export const SYNC_MATCHING_TEAMS_TO_LMS = gql`
  mutation SyncMatchingTeamsToLms($runId: String!) {
    syncMatchingTeamsToLms(runId: $runId) {
      ... on MatchingRunType {
        ${matchingRunFragment}
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface SubmitMatchingResponseResult {
  submitMatchingResponse: MatchingSuccess;
}

export const SUBMIT_MATCHING_RESPONSE = gql`
  mutation SubmitMatchingResponse($key: String!, $answers: [MatchingAnswerInput!]!) {
    submitMatchingResponse(key: $key, answers: $answers) {
      ok
      message
    }
  }
`;

export interface SendMatchingRemindersResult {
  sendMatchingReminders: MatchingSuccess;
}

export const SEND_MATCHING_REMINDERS = gql`
  mutation SendMatchingReminders($runId: String!) {
    sendMatchingReminders(runId: $runId) {
      ok
      message
    }
  }
`;

export interface DeleteMatchingRunResult {
  deleteMatchingRun: { ok: boolean; message: string };
}

export const DELETE_MATCHING_RUN = gql`
  mutation DeleteMatchingRun($runId: String!) {
    deleteMatchingRun(runId: $runId) {
      ... on MatchingSuccess {
        ok
        message
      }
      ... on OperationInfo {
        messages {
          kind
          message
          field
        }
      }
    }
  }
`;

export interface GetMatchingGroupCategoriesResult {
  matchingGroupCategories: GroupCategory[];
}

export const GET_MATCHING_GROUP_CATEGORIES = gql`
  query GetMatchingGroupCategories {
    matchingGroupCategories {
      id
      name
    }
  }
`;
